import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useNews from '../hooks/useNews';
import usePagination from '../hooks/usePagination';
import NewsGrid from '../components/news/NewsGrid';
import NewsPagination from '../components/news/NewsPagination';
import Loader, { SkeletonGrid } from '../components/common/Loader';
import ErrorMessage from '../components/common/ErrorMessage';
import { getCategoryTitle, getCategoryColor } from '../constants/categories';

const CategoryPage = ({ searchQuery }) => {
  const { categorySlug } = useParams();
  const { currentPage, goToPage, resetPage } = usePagination(1);
  const { news, loading, error, refetch } = useNews(categorySlug, currentPage);

  const categoryTitle = getCategoryTitle(categorySlug);
  const categoryColor = getCategoryColor(categorySlug);

  // Filter news by search query
  const filteredNews = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) {
      return news;
    }
    const query = searchQuery.toLowerCase();
    return news.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.source.toLowerCase().includes(query)
    );
  }, [news, searchQuery]);

  // Reset page when category changes
  React.useEffect(() => {
    resetPage();
  }, [categorySlug, resetPage]);

  if (loading && currentPage === 1) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4 animate-pulse"></div>
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-96 animate-pulse"></div>
        </div>
        <SkeletonGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Category Header */}
      <header className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div
            className="w-1.5 h-10 rounded-full"
            style={{ backgroundColor: categoryColor }}
          ></div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {categoryTitle}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 ml-5">
          {searchQuery 
            ? `"${searchQuery}" için ${filteredNews.length} haber bulundu`
            : `${categoryTitle} kategorisindeki en son haberler`
          }
        </p>
      </header>

      {/* News Grid */}
      {filteredNews.length === 0 ? (
        <ErrorMessage 
          message={searchQuery 
            ? 'Aramanızla eşleşen haber bulunamadı.' 
            : 'Bu kategoride henüz haber bulunmuyor.'
          } 
        />
      ) : (
        <>
          <NewsGrid news={filteredNews} category={categorySlug} />

          {/* Pagination */}
          {!searchQuery && (
            <NewsPagination
              currentPage={currentPage}
              onPageChange={goToPage}
              hasMore={news.length >= 10}
            />
          )}
        </>
      )}

      {loading && currentPage > 1 && (
        <div className="mt-8">
          <Loader text="Daha fazla haber yükleniyor..." />
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
